import { memo, useMemo } from 'react'

import type { FirstDataRenderedEvent, RowDataUpdatedEvent, RowGroupOpenedEvent, SideBarDef } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement, RefObject } from 'react'
import type { IUADashboardData, IUADashboardItem } from '~/types/user-acquisition'

import AnalyticsAggregator from '../../AnalyticsAggregator'
import columnDefs from '../../columns'
import { getRowClass } from '../../utils'
import SettingsComponent from '../Settings'

import Table from '~/components/Tables/Table'

import './styles.scss'

const ENTRIES_NAME = 'ua-table'

interface Props {
  dashboardData?: IUADashboardData
  gridRef: RefObject<AgGridReact>
  isFullScreen: boolean
  isLoading?: boolean
}

const sideBarDef: SideBarDef = {
  defaultToolPanel: 'filters',
  position: 'left',
  toolPanels: [
    {
      iconKey: 'columns',
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      minWidth: 225,
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressColumnExpandAll: true,
        suppressColumnFilter: true,
        suppressColumnSelectAll: true,
        suppressPivotMode: true,
        suppressPivots: true,
        suppressRowGroups: true,
        suppressValues: true,
      },
      width: 225,
    },
  ],
}

const statusBarDef = {
  statusPanels: [
    {
      align: 'left',
      statusPanel: 'agTotalAndFilteredRowCountComponent',
    },
    {
      statusPanel: 'agAggregationComponent',
      statusPanelParams: {
        aggFuncs: ['avg', 'sum'],
      },
    },
    { statusPanel: SettingsComponent },
  ],
}

const UADashboardTable = ({ dashboardData, gridRef, isFullScreen, isLoading }: Props): ReactElement => {
  const rowData = dashboardData?.items || []

  const columns = useMemo(() => columnDefs(dashboardData?.body.aggregations || []), [dashboardData?.body.aggregations])

  const handleRowGroupOpened = (event: RowGroupOpenedEvent<IUADashboardItem, any>) => event.api.autoSizeAllColumns()

  const handleDataUpdate = (event: FirstDataRenderedEvent | RowDataUpdatedEvent) => event.api.autoSizeAllColumns()

  return (
    <Table
      aggFuncs={AnalyticsAggregator}
      animateRows={false}
      autoGroupColumnDef={{
        headerName: 'Aggregations',
        pinned: 'left',
      }}
      autoSizeColumnToFit={false}
      cellSelection
      className="ua-dashboard-table"
      columnDefs={columns}
      containerStyle={{ height: isFullScreen ? '100%' : ' calc(100vh - 32rem)' }}
      defaultColDef={{
        flex: 1,
        resizable: true,
        sortable: true,
        suppressSizeToFit: true,
      }}
      domLayout="normal"
      enableCharts
      entriesName={ENTRIES_NAME}
      getRowClass={getRowClass}
      grandTotalRow="bottom"
      groupDisplayType="singleColumn"
      groupHideOpenParents={false}
      groupHideParentOfSingleChild={false}
      groupTotalRow={undefined}
      loading={isLoading}
      onFirstDataRendered={handleDataUpdate}
      onRowDataUpdated={handleDataUpdate}
      onRowGroupOpened={handleRowGroupOpened}
      ref={gridRef}
      rowBuffer={10}
      rowData={rowData}
      rowHeight={25}
      showOpenedGroup={false}
      sideBar={sideBarDef}
      statusBar={statusBarDef}
      suppressAggFuncInHeader
      suppressColumnVirtualisation
      suppressRowHoverHighlight
      withPagination={false}
    />
  )
}

UADashboardTable.displayName = 'UADashboardTable'

export default memo(UADashboardTable)
